import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

/**
 * PhotoViewerModal Component
 *
 * Full-screen modal for viewing photos with navigation
 */

const PhotoViewerModal = ({
  isOpen,
  onClose,
  photo,
  allPhotos = [],
  onDelete,
  onDownload
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPhoto, setCurrentPhoto] = useState(photo);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (photo && allPhotos.length > 0) {
      const index = allPhotos.findIndex(p => p.storagePath === photo.storagePath);
      setCurrentIndex(index >= 0 ? index : 0);
      setCurrentPhoto(allPhotos[index >= 0 ? index : 0]);
    }
  }, [photo, allPhotos]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleNext = useCallback(() => {
    if (currentIndex < allPhotos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentPhoto(allPhotos[currentIndex + 1]);
      setIsZoomed(false);
    }
  }, [currentIndex, allPhotos]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentPhoto(allPhotos[currentIndex - 1]);
      setIsZoomed(false);
    }
  }, [currentIndex, allPhotos]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleNext, handlePrevious]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!isOpen || !currentPhoto) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex flex-col">
      {/* Header */}
      <div className="bg-black bg-opacity-90 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm sm:text-lg truncate">
              {currentPhoto.category || 'Photo'}
            </h3>
            <p className="text-gray-400 text-xs sm:text-sm truncate">
              {formatDate(currentPhoto.uploadedAt)} • {formatFileSize(currentPhoto.fileSize)}
            </p>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
            {allPhotos.length > 1 && (
              <span className="text-white text-xs sm:text-sm px-2 sm:px-3 py-1 bg-gray-800 rounded-full">
                {currentIndex + 1} / {allPhotos.length}
              </span>
            )}

            <button
              onClick={() => onDownload && onDownload(currentPhoto)}
              className="p-1.5 sm:p-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Download"
            >
              <ArrowDownTrayIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>

            {onDelete && (
              <button
                onClick={() => {
                  onDelete(currentPhoto);
                  onClose();
                }}
                className="p-1.5 sm:p-2 text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                title="Delete"
              >
                <TrashIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            )}

            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 text-white hover:bg-gray-800 rounded-lg transition-colors"
              title="Close (Esc)"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Image Container */}
      <div className="flex-1 flex items-center justify-center p-2 sm:p-4 relative">
        {/* Previous Button */}
        {allPhotos.length > 1 && currentIndex > 0 && (
          <button
            onClick={handlePrevious}
            className="absolute left-2 sm:left-4 p-2 sm:p-3 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full transition-all z-10"
            title="Previous (←)"
          >
            <ChevronLeftIcon className="h-6 w-6 sm:h-8 sm:w-8" />
          </button>
        )}

        {/* Image */}
        <div
          className={`relative max-w-full max-h-full flex items-center justify-center ${
            isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
          }`}
          onClick={() => setIsZoomed(!isZoomed)}
        >
          <img
            src={currentPhoto.mediumUrl || currentPhoto.originalUrl}
            alt={currentPhoto.category}
            className={`max-w-full max-h-[calc(100vh-120px)] sm:max-h-[calc(100vh-200px)] object-contain transition-transform duration-300 ${
              isZoomed ? 'scale-150' : 'scale-100'
            }`}
            draggable={false}
          />
        </div>

        {/* Next Button */}
        {allPhotos.length > 1 && currentIndex < allPhotos.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-2 sm:right-4 p-2 sm:p-3 bg-black bg-opacity-50 hover:bg-opacity-75 text-white rounded-full transition-all z-10"
            title="Next (→)"
          >
            <ChevronRightIcon className="h-6 w-6 sm:h-8 sm:w-8" />
          </button>
        )}
      </div>

      {/* Thumbnail Strip (if multiple photos) */}
      {allPhotos.length > 1 && (
        <div className="bg-black bg-opacity-90 backdrop-blur-sm border-t border-gray-800 overflow-x-auto">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 sm:py-3">
            <div className="flex space-x-1.5 sm:space-x-2">
              {allPhotos.map((p, index) => (
                <button
                  key={p.storagePath}
                  onClick={() => {
                    setCurrentIndex(index);
                    setCurrentPhoto(allPhotos[index]);
                    setIsZoomed(false);
                  }}
                  className={`flex-shrink-0 relative ${
                    index === currentIndex
                      ? 'ring-2 ring-blue-500 ring-offset-1 sm:ring-offset-2 ring-offset-black'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={p.thumbUrl || p.mediumUrl || p.originalUrl}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-14 h-14 sm:w-20 sm:h-20 object-cover rounded"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Help text - hidden on mobile */}
      <div className="hidden sm:block absolute bottom-4 left-4 text-gray-400 text-xs bg-black bg-opacity-50 px-3 py-2 rounded">
        <p>Click image to zoom • Use ← → keys to navigate • Press Esc to close</p>
      </div>
    </div>
  );
};

export default PhotoViewerModal;
